import { prepareHipaaAuthorization } from './hipaaAssemblyService';
import { assemblyClientReadiness } from './hipaaReadiness';
/**
 * HIPAA Document Service
 * 
 * Manages HIPAA authorization documents for clients.
 * Handles general and provider-specific HIPAA forms.
 */

import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    collection,
    query,
    where,
    getDocs,
    writeBatch,
    deleteField,
    type WriteBatch,
    type UpdateData,
    Timestamp
} from 'firebase/firestore';
import { uploadHipaaSourceFile } from './hipaaSourceUpload';
import { sha256Hex } from '../utils/fileHash';
import { db } from '../config/firebase';
import { Document, Client, DocumentType } from '../types';
import { withErrorHandling } from '../utils/errorHandling';
import { logAudit } from './auditLogService';
import { logger } from '../utils/logger';
import { usesGeneralAuthorization, usesAssemblyAuthorization, selectGeneralAuthorization } from './generalAuthorization';
import { deriveClientHipaaFields, ClientHipaaDenormalization } from './hipaaSignatureState';

function timestampToMillis(value: unknown): number {
    if (!value || typeof value !== 'object') return 0;
    const ts = value as { toMillis?: () => number; seconds?: number; nanoseconds?: number };
    if (typeof ts.toMillis === 'function') return ts.toMillis();
    if (typeof ts.seconds === 'number') {
        return (ts.seconds * 1000) + Math.floor((ts.nanoseconds || 0) / 1_000_000);
    }
    return 0;
}

/**
 * HIPAA lookup result
 */
export interface HipaaLookupResult {
    document: Document | null;
    type: 'general' | 'provider_specific' | 'none';
    isAvailable: boolean;
    status: 'valid' | 'unsigned' | 'missing'; // NEW: Status code
}

/**
 * Outcome of the per-provider HIPAA overlay generation that runs during a
 * provider assignment. The assignment itself always succeeds; this captures
 * whether the auto-generated facility-keyed HIPAA was produced so the UI can
 * show a recovery banner instead of leaving the user staring at an un-overlaid
 * base PDF.
 */
export type HipaaGenerationStatus = 'ok' | 'deferred' | 'failed';

export interface HipaaGenerationOutcome {
    status: HipaaGenerationStatus;
    /** Stable code from the cloud function (`facility-name-missing`, etc.) — for analytics. */
    reason?: string;
    /** Plain-English message suitable to render directly in the UI banner. */
    userMessage?: string;
}

/**
 * Plain-English recovery copy for each known overlay-generation failure reason.
 * Law-firm staff should never see raw Firebase error strings — see CLAUDE.md
 * memory `feedback_usability`.
 */
export function describeHipaaGenerationFailure(reason: string | undefined): string {
    switch (reason) {
        case 'facility-name-missing':
            return "We couldn't find this facility's name. Open Provider Setup, set the facility name, then click Regenerate HIPAA.";
        case 'facility-address-missing':
            return "This facility has no address on file. Open Provider Setup, add the facility address, then click Regenerate HIPAA.";
        case 'needs-facility':
            return "We couldn't link this provider to a facility. Open Provider Setup, set the provider's primary facility, then click Regenerate HIPAA.";
        case 'facility-missing':
            return "We couldn't find the facility record for this provider. Re-check Provider Setup, then click Regenerate HIPAA.";
        case 'needs-base-hipaa':
            return "We couldn't find the signed HIPAA for this client. Re-upload it on the client's case, then click Regenerate HIPAA.";
        case 'overlay-review-required':
            return 'Review and approve it before sending: on this case’s practices list, click the refresh icon next to the provider and check every page. If text overlaps, correct the form or mapping in Settings → HIPAA.';
        // F-2: the facility block would cover the signed form's own text. Fixed on the form, not by retrying.
        case 'overlay-collision':
            return "The practice's name and address would print on top of text already on this client's signed HIPAA form, so it can't be prepared for this practice. This usually means the client signed a different form from the firm's own HIPAA form. Upload a copy signed on the firm's form on the client's page, or ask a firm manager to check Settings → HIPAA.";
        case 'needs-template':
            return 'Your HIPAA template setup is incomplete. Open Settings → HIPAA and finish placing the facility name and address fields.';
        case 'needs-facility-mapping':
            return "Your HIPAA template is missing the facility name field. Open Settings → HIPAA and place that field, then click Regenerate HIPAA.";
        case 'page-overflow':
            return "Some HIPAA fields are placed on a page that doesn't exist in your uploaded form. Open Settings → HIPAA and re-place those fields, then click Regenerate HIPAA.";
        // P1-1c: the server holds the overlay; retrying is held again, so point at the client page.
        case 'authorization-identity-mismatch':
            return "The signed authorization on file appears to be for a different patient. An attorney needs to review it on the client's page, or upload the correct signed authorization there.";
        default:
            return "We couldn't pre-fill HIPAA for this provider. Click Regenerate HIPAA to retry — if it keeps failing, contact support.";
    }
}

/**
 * Get all HIPAA documents for a client
 */
export async function getClientHipaaDocuments(
    firmId: string,
    clientId: string
): Promise<Document[]> {
    return withErrorHandling(async () => {
        const docsRef = collection(db, `firms/${firmId}/documents`);
        const q = query(
            docsRef,
            where('clientId', '==', clientId),
            where('documentType', 'in', ['hipaa_auth', 'hipaa_auth_general', 'hipaa_auth_provider', 'hipaa_auth_provider_signed', 'hipaa_auth_signed'])
        );

        const snapshot = await getDocs(q);
        const documents: Document[] = [];

        snapshot.forEach((doc) => {
            documents.push(doc.data() as Document);
        });

        logger.debug(`Found ${documents.length} HIPAA documents for selected client`);
        return documents;
    }, 'get client HIPAA documents') as Promise<Document[]>;
}

/**
 * Get provider-specific HIPAA for a specific provider.
 *
 * Two record shapes coexist after the facility-keyed migration:
 *   - new (post-fix): docs carry `applicableProviderIds: string[]`, one record per facility.
 *   - legacy: docs carry `applicableProviderId: string`, one record per provider.
 *
 * Single Firestore round-trip: fetch all provider-type HIPAAs for the client,
 * then filter in-memory by either field. The previous implementation used
 * `where('applicableProviderId', '==', providerId)` which silently misses
 * facility-keyed records where the providerId was added via arrayUnion (and
 * therefore lives in `applicableProviderIds` but not the singular field).
 */
export async function getProviderSpecificHipaa(
    firmId: string,
    clientId: string,
    providerId: string
): Promise<Document | null> {
    return withErrorHandling(async () => {
        const docsRef = collection(db, `firms/${firmId}/documents`);
        const q = query(
            docsRef,
            where('clientId', '==', clientId),
            where('documentType', 'in', ['hipaa_auth_provider', 'hipaa_auth_provider_signed'])
        );

        const snapshot = await getDocs(q);
        const rawDocs = snapshot.docs ?? [];
        const docs = rawDocs
            .map((d) => d.data() as Document)
            .filter((doc) =>
                (doc.applicableProviderIds?.includes(providerId) ?? false) ||
                doc.applicableProviderId === providerId
            )
            .filter((doc) => doc.protectedDocumentStatus !== 'replaced')
            .sort((a, b) => timestampToMillis(b.uploadedAt || b.createdAt) - timestampToMillis(a.uploadedAt || a.createdAt));

        if (docs.length === 0) {
            logger.debug(`No active provider-specific HIPAA for provider ${providerId}`);
            return null;
        }

        logger.debug(`Found provider-specific HIPAA for provider ${providerId}`);
        return docs[0];
    }, 'get provider-specific HIPAA') as Promise<Document | null>;
}

/**
 * Get the applicable HIPAA document for a provider
 * Priority: pinned > provider-specific > general
 */
export async function getApplicableHipaa(
    firmId: string,
    clientId: string,
    providerId: string,
    preferredDocumentId?: string | null
): Promise<HipaaLookupResult> {
    return withErrorHandling(async () => {
        if (await usesGeneralAuthorization(firmId)) {
            const [client, documents] = await Promise.all([getDoc(doc(db, `firms/${firmId}/clients/${clientId}`)), getClientHipaaDocuments(firmId, clientId)]);
            const base = selectGeneralAuthorization(client.data() as Client, documents);
            return { document: base, type: base ? 'general' : 'none', isAvailable: !!base && base.signatureStatus === 'signed',
                status: !base ? 'missing' : base.signatureStatus === 'signed' ? 'valid' : 'unsigned' } as HipaaLookupResult;
        }
        if (await usesAssemblyAuthorization(firmId)) {
            const client = (await getDoc(doc(db, `firms/${firmId}/clients/${clientId}`))).data() as Client;
            const versionId = client.hipaaAssemblyAssociation?.versionId;
            const version = versionId ? (await getDoc(doc(db, `firms/${firmId}/hipaaTemplateVersions/${versionId}`))).data()?.assembly : undefined;
            if (assemblyClientReadiness(client, version, true) !== 'ready-to-assemble') return { document: null, type: 'none', isAvailable: false, status: 'missing' };
            try {
                const packet = await prepareHipaaAuthorization({ firmId, clientId, providerId });
                const document = (await getDoc(doc(db, `firms/${firmId}/documents/${packet.documentId}`))).data() as Document | undefined;
                return { document: document ?? null, type: document ? 'provider_specific' : 'none', isAvailable: !!document, status: document ? 'valid' : 'missing' };
            } catch { return { document: null, type: 'none', isAvailable: false, status: 'missing' }; }
        }
        // 1. Check if there's a pinned/preferred HIPAA document
        if (preferredDocumentId) {
            const docRef = doc(db, `firms/${firmId}/documents/${preferredDocumentId}`);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const docData = docSnap.data() as Document;

                // VALIDATION: If pinned doc is provider-specific, ensure it covers the target provider.
                // New records use `applicableProviderIds` (array, one record per facility);
                // legacy records use the singular `applicableProviderId`.
                const matchesProvider =
                    !docData.providerSpecific
                    || (docData.applicableProviderIds?.includes(providerId) ?? false)
                    || docData.applicableProviderId === providerId;
                if (!matchesProvider) {
                    const coveredIds = docData.applicableProviderIds?.length
                        ? docData.applicableProviderIds.join(', ')
                        : (docData.applicableProviderId ?? 'unknown');
                    logger.warn(
                        `Pinned HIPAA ${preferredDocumentId} is provider-specific for [${coveredIds}] ` +
                        `but does not cover target provider ${providerId}. Falling back to hierarchy.`
                    );
                    // Don't use it, proceed to next tier in the hierarchy
                } else {
                    // Valid pinned document (either general, or provider-specific that matches)
                    const isUnsigned = docData.signatureStatus === 'unsigned';
                    const docType = docData.providerSpecific ? 'provider_specific' : 'general';

                    logger.info(
                        `Using pinned HIPAA document ${preferredDocumentId} ` +
                        `(type: ${docType}, status: ${docData.signatureStatus || 'unknown'})`
                    );

                    return {
                        document: docData,
                        type: docType,
                        isAvailable: !isUnsigned,
                        status: isUnsigned ? 'unsigned' : 'valid',
                    };
                }
            }
        }

        // 2. Check for provider-specific HIPAA
        const providerSpecific = await getProviderSpecificHipaa(firmId, clientId, providerId);
        if (providerSpecific) {
            logger.info(`Using provider-specific HIPAA for provider ${providerId}`);
            const isUnsigned = providerSpecific.signatureStatus === 'unsigned';
            logger.info(`Using provider-specific HIPAA for provider ${providerId} (status: ${providerSpecific.signatureStatus || 'unknown'})`);
            return {
                document: providerSpecific,
                type: 'provider_specific',
                isAvailable: !isUnsigned,
                status: isUnsigned ? 'unsigned' : 'valid',
            };
        }

        // 3. Fall back to general HIPAA
        const allHipaa = await getClientHipaaDocuments(firmId, clientId);
        const generalHipaa = allHipaa.find(
            (doc) =>
                (doc.documentType === 'hipaa_auth' || doc.documentType === 'hipaa_auth_general' || doc.documentType === 'hipaa_auth_signed') &&
                !doc.providerSpecific
        );

        if (generalHipaa) {
            logger.info(`Using general HIPAA for provider ${providerId}`);
            const isUnsigned = generalHipaa.signatureStatus === 'unsigned';
            logger.info(`Using general HIPAA for provider ${providerId} (status: ${generalHipaa.signatureStatus || 'unknown'})`);
            return {
                document: generalHipaa,
                type: 'general',
                isAvailable: !isUnsigned,
                status: isUnsigned ? 'unsigned' : 'valid',
            };
        }

        // 4. No HIPAA available
        logger.warn(`No applicable HIPAA found for client ${clientId}, provider ${providerId}`);
        return {
            document: null,
            type: 'none',
            isAvailable: false,
            status: 'missing',
        };
    }, 'get applicable HIPAA') as Promise<HipaaLookupResult>;
}

/**
 * Check if applicable HIPAA exists for a provider
 * Used for validation before provider assignment or request creation
 */
export async function checkHipaaAvailability(
    firmId: string,
    clientId: string,
    providerId: string
): Promise<{
    hasHipaa: boolean;
    hipaaType: 'general' | 'provider_specific' | 'none';
    documentId: string | null;
    status: 'valid' | 'unsigned' | 'missing';
}> {
    const result = await getApplicableHipaa(firmId, clientId, providerId);

    return {
        hasHipaa: result.isAvailable,
        hipaaType: result.type,
        documentId: result.document?.documentId || null,
        status: result.status,
    };
}

/**
 * Batch check HIPAA availability for multiple providers
 * Optimized to perform a single query for all client documents
 */
export async function checkHipaaAvailabilityBatch(
    firmId: string,
    clientId: string,
    providerIds: string[]
): Promise<Record<string, {
    hasHipaa: boolean;
    hipaaType: 'general' | 'provider_specific' | 'none';
    documentId: string | null;
    status: 'valid' | 'unsigned' | 'missing';
    document?: Document;
}>> {
    return withErrorHandling(async () => {
        if (await usesGeneralAuthorization(firmId)) {
            const [client, documents] = await Promise.all([getDoc(doc(db, `firms/${firmId}/clients/${clientId}`)), getClientHipaaDocuments(firmId, clientId)]);
            const base = selectGeneralAuthorization(client.data() as Client, documents);
            return Object.fromEntries(providerIds.map(id => [id, { hasHipaa: !!base && base.signatureStatus === 'signed',
                hipaaType: base ? 'general' : 'none', documentId: base?.documentId ?? null,
                status: !base ? 'missing' : base.signatureStatus === 'signed' ? 'valid' : 'unsigned', ...(base ? { document: base } : {}) }]));
        }
        if (await usesAssemblyAuthorization(firmId)) {
            return Object.fromEntries(await Promise.all(providerIds.map(async id => {
                const result = await getApplicableHipaa(firmId, clientId, id);
                return [id, { hasHipaa: result.isAvailable, hipaaType: result.type, documentId: result.document?.documentId ?? null, status: result.status, ...(result.document ? { document: result.document } : {}) }];
            })));
        }
        // 1. Fetch all documents for client (single query)
        const allDocs = await getClientHipaaDocuments(firmId, clientId);

        // 2. Process each providerId
        const results: Record<string, {
            hasHipaa: boolean;
            hipaaType: 'general' | 'provider_specific' | 'none';
            documentId: string | null;
            status: 'valid' | 'unsigned' | 'missing';
            document?: Document;
        }> = {};

        // Pre-calculate general docs to avoid searching every time
        // General HIPAA must NOT be provider specific and NOT replaced
        const generalHipaa = allDocs.find(
            (doc) => (
                doc.documentType === 'hipaa_auth' ||
                doc.documentType === 'hipaa_auth_general' ||
                doc.documentType === 'hipaa_auth_signed'
            ) && !doc.providerSpecific && doc.protectedDocumentStatus !== 'replaced'
        );

        for (const pid of providerIds) {
            // Check provider specific first (and NOT replaced).
            // New facility-keyed records use `applicableProviderIds` (array); legacy
            // records use the singular `applicableProviderId`. Match either.
            const providerSpecific = allDocs
                .filter((doc) =>
                    (doc.documentType === 'hipaa_auth_provider' || doc.documentType === 'hipaa_auth_provider_signed') &&
                    ((doc.applicableProviderIds?.includes(pid) ?? false) || doc.applicableProviderId === pid) &&
                    doc.protectedDocumentStatus !== 'replaced'
                )
                .sort((a, b) => timestampToMillis(b.uploadedAt || b.createdAt) - timestampToMillis(a.uploadedAt || a.createdAt))[0];

            if (providerSpecific) {
                const isUnsigned = providerSpecific.signatureStatus === 'unsigned';
                results[pid] = {
                    hasHipaa: !isUnsigned,
                    hipaaType: 'provider_specific',
                    documentId: providerSpecific.documentId,
                    status: isUnsigned ? 'unsigned' : 'valid',
                    document: providerSpecific
                };
                continue;
            }

            if (generalHipaa) {
                const isUnsigned = generalHipaa.signatureStatus === 'unsigned';
                results[pid] = {
                    hasHipaa: !isUnsigned,
                    hipaaType: 'general',
                    documentId: generalHipaa.documentId,
                    status: isUnsigned ? 'unsigned' : 'valid',
                    document: generalHipaa
                };
                continue;
            }

            // No applicable HIPAA found
            results[pid] = {
                hasHipaa: false,
                hipaaType: 'none',
                documentId: null,
                status: 'missing'
            };
        }

        return results;
    }, 'check HIPAA availability batch') as Promise<Record<string, {
        hasHipaa: boolean;
        hipaaType: 'general' | 'provider_specific' | 'none';
        documentId: string | null;
        status: 'valid' | 'unsigned' | 'missing';
        document?: Document;
    }>>;
}

/**
 * Supersede a client's prior signed general HIPAA authorization(s) when a new signed
 * general HIPAA replaces them (s34-3, Option A — one governing authorization per client).
 *
 * Flags every prior non-replaced general base doc AND every auto-generated per-facility
 * derivative (`hipaa_auth_provider_signed`) as `protectedDocumentStatus: 'replaced'`, so:
 *   - the general-HIPAA readers (getApplicableHipaa / checkHipaaAvailabilityBatch, which
 *     filter out 'replaced') deterministically resolve the single new authorization; and
 *   - the send-time selector (findFacilityHipaaDocument) skips the stale derivatives and
 *     re-overlays from the NEW base, so a corrected authorization actually propagates.
 *
 * Non-destructive: nothing is deleted. Superseded docs and their storage files are
 * retained (flagged), preserving the audit trail. Already-SENT requests are untouched
 * (they resolved and delivered their PDF at send time and hold no reference to these
 * docs); not-yet-sent requests regenerate from the new base on their next send.
 *
 * Returns the id of the most-recent prior general doc (for `replacesDocument` on the new
 * doc), or null if the client had no prior general authorization.
 */
interface HipaaSupersedeSelection {
    /** Prior docs to flag `protectedDocumentStatus: 'replaced'`. */
    toSupersede: Document[];
    /** Most-recent prior general doc id, for `replacesDocument` on the new doc. */
    priorGeneralPrimaryId: string | null;
    priorGeneralCount: number;
    priorDerivativeCount: number;
}

/**
 * Pure selector: from a client's existing HIPAA docs, pick the ones a new signed general
 * HIPAA supersedes — every prior non-replaced general base doc, plus every auto-generated
 * per-facility derivative (`hipaa_auth_provider_signed`). Manually-uploaded
 * provider-specific forms (a separate authorization) are intentionally left alone.
 */
function selectHipaaToSupersede(existing: Document[], newDocId: string): HipaaSupersedeSelection {
    const priorGeneral = existing.filter(
        (d) =>
            d.documentId !== newDocId &&
            !d.providerSpecific &&
            (d.documentType === 'hipaa_auth' ||
                d.documentType === 'hipaa_auth_general' ||
                d.documentType === 'hipaa_auth_signed') &&
            d.protectedDocumentStatus !== 'replaced',
    );
    const priorDerivatives = existing.filter(
        (d) =>
            d.documentId !== newDocId &&
            d.documentType === 'hipaa_auth_provider_signed' &&
            d.protectedDocumentStatus !== 'replaced',
    );
    const priorGeneralPrimaryId =
        [...priorGeneral].sort(
            (a, b) =>
                timestampToMillis(b.uploadedAt || b.createdAt) -
                timestampToMillis(a.uploadedAt || a.createdAt),
        )[0]?.documentId ?? null;

    return {
        toSupersede: [...priorGeneral, ...priorDerivatives],
        priorGeneralPrimaryId,
        priorGeneralCount: priorGeneral.length,
        priorDerivativeCount: priorDerivatives.length,
    };
}

/**
 * Flag a client's prior signed general HIPAA authorization(s) — and their auto-generated
 * per-facility derivatives — as superseded when a new signed general HIPAA replaces them
 * (s34-3, Option A: one governing authorization per client).
 *
 * Flagging (not deleting) means:
 *   - the general-HIPAA readers (getApplicableHipaa / checkHipaaAvailabilityBatch, which
 *     filter out 'replaced') deterministically resolve the single new authorization; and
 *   - the send-time selector (findFacilityHipaaDocument) skips the stale derivatives and
 *     re-overlays from the NEW base, so a corrected authorization actually propagates.
 *
 * Non-destructive: superseded docs and their storage files are retained (audit trail).
 * Already-SENT requests are untouched (they resolved and delivered their PDF at send time
 * and hold no reference to these docs); not-yet-sent requests regenerate on next send.
 */
function stagePriorGeneralHipaaReplacement(
    firmId: string,
    newDocId: string,
    selection: HipaaSupersedeSelection,
    batch: WriteBatch,
): void {
    for (const d of selection.toSupersede) {
        batch.update(doc(db, `firms/${firmId}/documents/${d.documentId}`), {
            protectedDocumentStatus: 'replaced',
            replacedBy: newDocId,
            replacedAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
        });
    }
}

/**
 * THE single writer (s36) for the client-level HIPAA denormalizations. Reads the
 * client's documents (authoritative), recomputes signatureStatus / hipaaFormsCount
 * / hasProviderSpecificHipaa via the one shared definition, and persists them.
 *
 * Call this after ANY change to a client's HIPAA documents (upload, replace,
 * signature confirmation) instead of incrementing counters at the call site — an
 * increment that isn't paired with a decrement is exactly what drifted the count
 * on replace (s35-3). Recompute-from-documents cannot drift.
 */
export async function syncClientHipaaDenormalizedFields(
    firmId: string,
    clientId: string,
    atomic?: { batch: WriteBatch; documents: Document[]; clientUpdates: UpdateData<Client> },
): Promise<ClientHipaaDenormalization> {
    const docs = atomic?.documents ?? await getClientHipaaDocuments(firmId, clientId);
    const fields = deriveClientHipaaFields(docs);
    if (docs.some(d => d.hipaaDocumentRole)) return fields; // Assembly promotion owns protected current-source caches.
    const clientRef = doc(db, `firms/${firmId}/clients/${clientId}`);
    const updates = { ...atomic?.clientUpdates, ...fields, updatedAt: Timestamp.now() };
    if (atomic) atomic.batch.update(clientRef, updates);
    else await updateDoc(clientRef, updates);
    logger.info(
        `Synced client HIPAA denormalization for ${clientId}: ` +
        `signatureStatus=${fields.signatureStatus}, forms=${fields.hipaaFormsCount}, ` +
        `providerSpecific=${fields.hasProviderSpecificHipaa}`,
    );
    return fields;
}

/**
 * Upload additional HIPAA form after client creation
 */
export interface UploadHipaaOptions {
    type: 'general' | 'provider_specific';
    providerId?: string;
    ocrMetadata?: {
        detectedProviderName?: string | null;
        confidence?: number;
        [key: string]: unknown; // Use unknown instead of any
    };
    userOverride?: boolean;
    /**
     * Whether the uploaded PDF is already signed by the patient.
     * Defaults to true (most uploads are pre-signed).
     * Pass false when uploading an unsigned template that will be signed later.
     */
    isSigned?: boolean;
    /**
     * F-P1-1: the server's identity verdict for this file against this client, and
     * whether an attorney accepted a mismatch. Recorded on the replace audit entry.
     * F-12: `override` (the attorney's reason and when they accepted) is also stored
     * on the new document so the history can mark it.
     */
    identity?: {
        verdict: 'match' | 'mismatch' | 'incomplete';
        overridden: boolean;
        override?: { reason: string; overriddenAt: Date };
    };
}

export async function uploadAdditionalHipaa(
    firmId: string,
    clientId: string,
    userId: string,
    userName: string,
    file: File,
    options: UploadHipaaOptions
): Promise<string> {
    return withErrorHandling(async () => {
        // Validate inputs
        if (options.type === 'provider_specific' && !options.providerId) {
            throw new Error('Provider ID required for provider-specific HIPAA');
        }

        const uploaded = await uploadHipaaSourceFile(firmId, clientId, file);
        const storagePath = uploaded.storagePath;
        const sha256 = await sha256Hex(file);

        // Get client data
        const clientRef = doc(db, `firms/${firmId}/clients/${clientId}`);
        const clientSnap = await getDoc(clientRef);
        if (!clientSnap.exists()) {
            throw new Error('Client not found');
        }
        const client = clientSnap.data() as Client;
        const firm = await getDoc(doc(db, `firms/${firmId}`));
        if (firm.data()?.settings?.hipaaDocumentWorkflow === 'assemble') {
            const { prepareHipaaSource } = await import('./hipaaAssemblyService');
            const candidate = await prepareHipaaSource({ firmId, clientId, storagePath,
                expectedSourceRevision: client.hipaaSourceRevision ?? 0, expectedAssociationRevision: client.hipaaAssociationRevision ?? 0 });
            return candidate.documentId;
        }


        // Create document record
        const documentType: DocumentType = options.type === 'general'
            ? 'hipaa_auth_general'
            : 'hipaa_auth_provider';

        const docRef = doc(collection(db, `firms/${firmId}/documents`));

        // s34-3 (Option A): a new SIGNED general HIPAA replaces the client's prior
        // governing authorization. Select the docs to supersede BEFORE creating the new
        // one — `replacesDocument` is only writable at create time (Firestore rules), so
        // we need the prior general id now. Only a confidently-signed general upload
        // replaces; an unsigned/uncertain upload is additive and leaves the prior signed
        // authorization governing.
        const isReplacingUpload = options.type === 'general' && (options.isSigned ?? true);
        let supersedeSelection: HipaaSupersedeSelection = {
            toSupersede: [],
            priorGeneralPrimaryId: null,
            priorGeneralCount: 0,
            priorDerivativeCount: 0,
        };
        let existingHipaa: Document[] = [];
        if (isReplacingUpload) {
            existingHipaa = await getClientHipaaDocuments(firmId, clientId);
            supersedeSelection = selectHipaaToSupersede(existingHipaa, docRef.id);
        }
        const acceptedOverride = options.identity?.verdict === 'mismatch' && options.identity.overridden
            ? options.identity.override
            : undefined;
        const identityOverride = acceptedOverride
            ? { reason: acceptedOverride.reason, overriddenAt: Timestamp.fromDate(acceptedOverride.overriddenAt) }
            : undefined;
        const documentData: Omit<Document, 'documentId'> = {
            firmId,
            clientId,
            clientName: `${client.firstName} ${client.lastName}`,
            providerId: null,
            providerName: null,
            requestLogId: null,
            fileName: file.name,
            fileType: file.type,
            fileSizeBytes: file.size,
            storagePath,
            documentType,
            documentCategory: 'Legal/Authorization',

            // HIPAA-specific fields
            providerSpecific: options.type === 'provider_specific',
            applicableProviderId: options.providerId || null,
            applicableProviderIds: options.type === 'provider_specific' && options.providerId
                ? [options.providerId]
                : null,
            detectedProviderName: options.ocrMetadata?.detectedProviderName || null,
            detectedProviderConfidence: options.ocrMetadata?.confidence || null,
            userConfirmedType: true,
            markedAsGeneralDespiteProvider: options.userOverride || false,
            requiresAuditReview: options.userOverride || false,

            uploadedAt: Timestamp.now(),
            uploadedBy: userId,
            uploadedByName: userName,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            receivedDate: Timestamp.now(),
            status: 'uploaded',
            processingError: null,
            notes: null,
            tags: [],
            viewedBy: [],
            viewCount: 0,
            lastViewedAt: null,
            isProtected: true,
            protectedDocumentStatus: 'active',
            signatureStatus: (options.isSigned ?? true) ? 'signed' : 'unsigned',

            // Missing fields from Document interface
            documentDate: null,
            pageCount: null,
            thumbnailUrl: null,
            extractedText: null,
            replacedBy: null,
            replacedAt: null,
            replacesDocument: supersedeSelection.priorGeneralPrimaryId,
            ...(sha256 ? { sha256 } : {}),
            // F-12: firestore.rules accept this only when the server recorded the
            // override for (client, sha256), so it can't be claimed for other bytes.
            ...(sha256 && identityOverride ? { identityOverride } : {}),
        };

        const newDocument = { documentId: docRef.id, ...documentData };
        if (isReplacingUpload) {
            // Publish the replacement, its history links, source pointer, and derived
            // client fields together. A failed commit leaves the prior authority intact.
            const batch = writeBatch(db);
            batch.set(docRef, newDocument);
            stagePriorGeneralHipaaReplacement(firmId, docRef.id, supersedeSelection, batch);
            const replacedIds = new Set(supersedeSelection.toSupersede.map(d => d.documentId));
            const afterReplacement: Document[] = [
                ...existingHipaa.filter(d => d.documentId !== docRef.id).map(d => replacedIds.has(d.documentId)
                    ? { ...d, protectedDocumentStatus: 'replaced' as const } : d),
                newDocument,
            ];
            await syncClientHipaaDenormalizedFields(firmId, clientId, {
                batch, documents: afterReplacement,
                clientUpdates: {
                    hipaaAuthorizationStoragePath: storagePath,
                    hipaaAuthorizationUrl: deleteField(),
                },
            });
            await batch.commit();
            // F-P1-1: every replace of a governing authorization leaves an audit entry.
            if (supersedeSelection.priorGeneralCount > 0) {
                await logAudit({
                    firmId,
                    action: 'replace_hipaa_auth',
                    resourceType: 'client',
                    resourceId: clientId,
                    details: {
                        documentId: docRef.id,
                        replacesDocument: supersedeSelection.priorGeneralPrimaryId,
                        identityVerdict: options.identity?.verdict ?? 'not-checked',
                        identityOverride: options.identity?.overridden === true,
                    },
                    success: true,
                });
            }
        } else {
            await setDoc(docRef, newDocument);
            await syncClientHipaaDenormalizedFields(firmId, clientId);
        }

        logger.info(`Additional HIPAA form uploaded for client ${clientId}: ${documentType}`);
        return docRef.id;
    }, 'upload additional HIPAA') as Promise<string>;
}
